import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiSave, FiSettings, FiImage, FiTrash2, FiLogOut, FiAlertCircle, FiCheckCircle, FiAlertTriangle, FiX, FiTrendingUp, FiMail } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import companyService from '../../services/company.service';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import SectionHelpModal from '../../components/common/SectionHelpModal';
import AppTour from '../../components/common/AppTour';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import { Modal } from 'react-bootstrap';

const Settings = () => {
    const { user } = useAuth();
    const { triggerRefresh } = useNotifications();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [company, setCompany] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [gmailBannerDismissed, setGmailBannerDismissed] = useState(
        () => sessionStorage.getItem('gmailBannerDismissed_settings') === 'true'
    );
    const [runTour, setRunTour] = useState(false);

    // Estados para el recorte de imagen
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [finalCroppedFile, setFinalCroppedFile] = useState(null);



    const { register, handleSubmit, setValue, getValues, watch, formState: { errors } } = useForm();
    const logoFile = watch('logo_file');

    const insertTag = (fieldName, tag) => {
        // Obtenemos el elemento del DOM para manipular la posición del cursor
        const textarea = document.querySelector(`textarea[name="${fieldName}"]`);

        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value;
            const before = text.substring(0, start);
            const after = text.substring(end, text.length);
            const newValue = before + tag + after;

            // Actualizamos el estado del formulario
            setValue(fieldName, newValue, { shouldValidate: true, shouldDirty: true });

            // Re-enfocamos y movemos el cursor después del tag insertado
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + tag.length, start + tag.length);
            }, 0);
        } else {
            // Fallback si no encontramos el DOM (raro)
            const current = getValues(fieldName) || '';
            setValue(fieldName, current + tag, { shouldValidate: true, shouldDirty: true });
        }
    };

    useEffect(() => {
        loadCompany();

        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'configuracion') {
            setRunTour(true);
        }
    }, [location]);


    const handleLinkGmail = async () => {
        try {
            setSaving(true);
            const data = await companyService.initLinkGmail();
            if (data.authorization_url) {
                window.location.href = data.authorization_url;
            }
        } catch (error) {
            console.error(error);
            setError("Error iniciando vinculación con Google");
            setSaving(false);
        }
    };

    const handleUnlinkGmail = async () => {
        if (!window.confirm("¿Seguro que deseas desvincular tu cuenta de Google? Volverás a usar el sistema de correo predeterminado.")) {
            return;
        }

        try {
            setSaving(true);
            await companyService.unlinkGmail();
            setSuccess("Cuenta desvinculada correctamente.");
            loadCompany(); // Recargar estado
        } catch (error) {
            console.error(error);
            setError("Error al desvincular cuenta.");
        } finally {
            setSaving(false);
        }
    };

    // Actualizar previsualización del logo y disparar modal de recorte
    useEffect(() => {
        if (logoFile && logoFile.length > 0) {
            const file = logoFile[0];
            // Solo si es un archivo nuevo (no el que ya recortamos)
            if (file !== finalCroppedFile) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImageToCrop(reader.result);
                    setShowCropModal(true);
                };
                reader.readAsDataURL(file);
            }
        }
    }, [logoFile, finalCroppedFile]);

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleConfirmCrop = async () => {
        try {
            setSaving(true);
            const croppedImageBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
            const file = new File([croppedImageBlob], 'logo_recortado.png', { type: 'image/png' });
            
            // Actualizar preview local y cerrar modal de inmediato para dar respuesta visual
            const localUrl = URL.createObjectURL(croppedImageBlob);
            setLogoPreview(localUrl);
            setShowCropModal(false);
            setImageToCrop(null);
            setValue('logo_file', null); // Limpiar el campo del formulario para evitar bucles

            // Auto-guardar solo el logo en el servidor
            const formData = new FormData();
            formData.append('logo', file);
            
            await companyService.update(company.id, formData);
            
            setFinalCroppedFile(file);
            setSuccess("Logo actualizado correctamente.");
            
            // Sincronizar Header y otros componentes
            triggerRefresh();
            loadCompany(true); // LLamada "silenciosa": solo actualiza logo, no resetea el formulario
            
        } catch (e) {
            console.error(e);
            setError("Error al recortar o guardar el logo");
            setShowCropModal(false);
        } finally {
            setSaving(false);
        }
    };

    const loadCompany = async (isSilent = false) => {
        try {
            if (!isSilent) setLoading(true);
            const data = await companyService.getMyCompany();
            setCompany(data);

            if (!isSilent) {
                // Setear valores del formulario solo si no es una actualización silenciosa
                setValue('nombre', data.nombre);
                setValue('rut', data.rut);
                setValue('direccion', data.direccion);
                setValue('telefono', data.telefono);
                
                // Email de contacto por defecto: si la empresa no tiene, se usa el del usuario logueado
                const defaultEmail = data.email || user?.email || '';
                setValue('email', defaultEmail);
            }

            if (data.logo) {
                setLogoPreview(data.logo);
            }


        } catch (err) {
            console.error('Error loading company:', err);
            setError('Error al cargar datos de la empresa');
        } finally {
            setLoading(false);
        }
    };



    const onSubmit = async (data) => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('nombre', data.nombre);
            formData.append('direccion', data.direccion);
            formData.append('telefono', data.telefono);
            formData.append('email', data.email);

            // Usar el logo recortado si existe
            if (finalCroppedFile) {
                formData.append('logo', finalCroppedFile);
            } else if (data.logo_file && data.logo_file.length > 0) {
                formData.append('logo', data.logo_file[0]);
            }

            const updatedCompany = await companyService.update(company.id, formData);
            setCompany(updatedCompany);

            setSuccess('Configuración guardada correctamente.');
            triggerRefresh(); // Sincronizar cambios visuales

        } catch (err) {
            console.error('Error updating company:', err);
            setError('Error al guardar cambios.');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveLogo = async () => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar el logo?')) {
            return;
        }

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('logo', ''); // Enviar string vacío para eliminar logo

            await companyService.update(company.id, formData);
            
            setLogoPreview(null);
            setSuccess('Logo eliminado correctamente.');

            // Sincronizar Header y otros componentes sin recargar página
            triggerRefresh();
            loadCompany(true); // Actualización silenciosa (mantiene campos del formulario)

        } catch (err) {
            console.error('Error removing logo:', err);
            setError('Error al eliminar logo.');
        } finally {
            setSaving(false);
        }
    };

    const configuracionTourSteps = [
        {
            element: '#tour-configuraciones-header',
            intro: '<strong>Configuración de Empresa</strong>Aquí guardarás la información de tu negocio. Son los datos que verán tus clientes en cada cotización que envíes.',
            position: 'bottom',
        },
        {
            element: '#tour-configuracion-correo',
            intro: '<strong>Vincula tu Correo</strong>Conecta tu cuenta de Gmail aquí. Esto permite al sistema enviar cotizaciones directamente desde tu propio correo.',
            position: 'top',
        },
        {
            element: '#tour-configuracion-logo',
            intro: '<strong>Tu Logotipo</strong>Sube el logo de tu negocio para que tus presupuestos se vean profesionales. ¡Ahora sí, vamos a conocer CotizaPlus!',
            position: 'right',
        }
    ];

    const handleTourComplete = () => {
        // Redirigimos al módulo estrella, para seguir el viaje adentro.
        window.location.href = '/app/cotizaplus?tour=dashboard';
    };

    if (loading) return <div className="loading-overlay"><div className="spinner"></div></div>;

    return (
        <div className="container-custom fade-in-up" style={{ maxWidth: '1000px', width: '100%', margin: '0 auto', overflowX: 'hidden', overflowY: 'auto', paddingBottom: '80px' }}>
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={configuracionTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Entrar a CotizaPlus' }}
                    onTourComplete={handleTourComplete}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '0', borderBottom: 'none' }} id="tour-configuraciones-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Configuración de Empresa</h2>
                        <SectionHelpModal
                            title="¿Qué debo configurar aquí?"
                            content={
                                <div>
                                    <p>Esta es la página más importante para la imagen de tu negocio. Aquí ajustas la información que verán tus clientes:</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Tu Marca Personal (Logo):</strong> Sube aquí el logo de tu negocio. IdevPlus lo tomará automáticamente y lo imprimirá en la cabecera de todas las cotizaciones PDF que generes.</li>
                                        <li><strong>Tu Correo Formal (Google):</strong> Es OBLIGATORIO vincular tu cuenta de Gmail o Google Workspace. Al hacerlo, le das permiso a IdevPlus para que envíe los correos formales a tus clientes directo desde tu propia dirección de correo.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Personaliza tu marca y portal de autoatención</p>
                </div>
            </div>

            {/* Banner persistente de Gmail */}
            {user?.rol === 'ADMIN' && company && !company.usa_gmail_propio && !gmailBannerDismissed && (
                <div className="gmail-banner">
                    <div className="gmail-banner-content">
                        <div className="gmail-banner-icon">
                            <FiAlertTriangle size={20} />
                        </div>
                        <div>
                            <strong className="gmail-banner-title">
                                Gmail aún no vinculado
                            </strong>
                            <p className="gmail-banner-text gmail-banner-text-full">
                                Para enviar cotizaciones, gestionar empleados y enviar correos desde tu empresa, es OBLIGATORIO vincular una cuenta de Google.
                            </p>
                            <p className="gmail-banner-text gmail-banner-text-short">
                                Vincula Google para usar todas las funciones.
                            </p>
                        </div>
                    </div>
                    <div className="gmail-banner-actions">
                        <button
                            className="gmail-banner-close"
                            onClick={() => {
                                sessionStorage.setItem('gmailBannerDismissed_settings', 'true');
                                setGmailBannerDismissed(true);
                            }}
                            title="Cerrar alerta"
                        >
                            <FiX size={18} />
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #ec4899 100%)',
                    borderRadius: '16px',
                    padding: '18px 24px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    boxShadow: '0 6px 20px rgba(239, 68, 68, 0.2)',
                    color: 'white'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <FiAlertCircle size={20} />
                    </div>
                    <span style={{ fontSize: '14px', lineHeight: '1.5' }}>{error}</span>
                </div>
            )}
            {success && (
                <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                    borderRadius: '16px',
                    padding: '18px 24px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    boxShadow: '0 6px 20px rgba(16, 185, 129, 0.2)',
                    color: 'white'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <FiCheckCircle size={20} />
                    </div>
                    <span style={{ fontSize: '14px', lineHeight: '1.5' }}>{success}</span>
                </div>
            )}

            {/* Formulario Global */}
            <form onSubmit={handleSubmit(onSubmit)} className="settings-grid">
                
                {/* 1. SECCIÓN IDENTIDAD (Branding) */}
                <div className="settings-section card-custom">
                    <h3 className="section-title">
                        <FiImage /> Identidad de Marca
                    </h3>
                    <div className="branding-container">
                        <div id="tour-configuracion-logo" className="logo-upload-zone">
                            <div className="logo-preview-circle">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="Logo" />
                                ) : (
                                    <div className="no-logo">
                                        <FiImage size={40} />
                                        <span>Sin Logo</span>
                                    </div>
                                )}
                            </div>
                            <div className="logo-actions">
                                <label className="btn-action btn-upload">
                                    Cambiar Logo
                                    <input type="file" accept="image/*" {...register('logo_file')} style={{ display: 'none' }} />
                                </label>
                                {logoPreview && (
                                    <button type="button" onClick={handleRemoveLogo} className="btn-action btn-remove" disabled={saving}>
                                        <FiTrash2 size={14} /> Quitar
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="brand-name-zone">
                            <div className="form-group-custom">
                                <label className="form-label-custom">Nombre de la Empresa *</label>
                                <input
                                    {...register('nombre', { required: 'El nombre es obligatorio' })}
                                    className="form-control-custom"
                                    placeholder="Ej: Mi Negocio S.A."
                                />
                                {errors.nombre && <span className="form-error">{errors.nombre.message}</span>}
                            </div>
                            <p className="text-muted small">Este nombre aparecerá en la cabecera de tus cotizaciones.</p>
                        </div>
                    </div>
                </div>

                {/* 2. SECCIÓN DATOS DE NEGOCIO */}
                <div className="settings-section card-custom">
                    <h3 className="section-title">
                        <FiSettings /> Datos de Contacto y Facturación
                    </h3>
                    <div className="grid-2-columns">
                        <div className="form-group-custom">
                            <label className="form-label-custom">RUT (Identificador)</label>
                            <input {...register('rut')} className="form-control-custom" disabled style={{ backgroundColor: '#f9fafb' }} />
                        </div>
                        <div className="form-group-custom">
                            <label className="form-label-custom">Teléfono de contacto</label>
                            <input {...register('telefono')} className="form-control-custom" placeholder="+56 9 ..." />
                        </div>
                    </div>
                    <div className="form-group-custom mt-3">
                        <label className="form-label-custom">Dirección Comercial</label>
                        <input {...register('direccion')} className="form-control-custom" placeholder="Calle, Número, Ciudad" />
                    </div>
                </div>

                {/* 3. SECCIÓN COMUNICACIONES (Email + Google) */}
                <div className="settings-section card-custom" id="tour-configuracion-correo">
                    <h3 className="section-title">
                        <FiMail /> Comunicaciones y Envío
                    </h3>
                    
                    <div className="comm-grid">
                        <div className="email-config">
                            <label className="form-label-custom">Email de contacto en cotizaciones</label>
                            <input
                                {...register('email')}
                                className="form-control-custom"
                                placeholder="email@ejemplo.com"
                            />
                            <p className="text-muted small mt-1">Es el correo que verán tus clientes para contactarte.</p>
                        </div>

                        <div className="google-integration-card">
                            <div className="integration-status">
                                {company?.usa_gmail_propio ? (
                                    <div className="google-status-box connected">
                                        <div className="google-brand-icon">
                                            <FcGoogle size={32} />
                                        </div>
                                        <div className="google-info">
                                            <div className="google-label">Google Workspace / Gmail</div>
                                            <div className="google-email">{company.email_gmail_propio}</div>
                                            <div className="google-badge">✓ Vinculado correctamente</div>
                                        </div>
                                        <button type="button" onClick={handleUnlinkGmail} className="btn-google-unlink" disabled={saving}>
                                            Desvincular
                                        </button>
                                    </div>
                                ) : (
                                    <div className="google-status-box disconnected">
                                        <div className="google-brand-icon">
                                            <FcGoogle size={32} />
                                        </div>
                                        <div className="google-info">
                                            <div className="google-label">Vincular Correo Google</div>
                                            <p className="google-text">Envía cotizaciones directamente desde tu cuenta corporativa.</p>
                                        </div>
                                        <button type="button" onClick={handleLinkGmail} className="btn-google-connect-new" disabled={saving}>
                                            Conectar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="settings-actions">
                    <button type="submit" className="btn-save-all" disabled={saving}>
                        <FiSave /> {saving ? 'Guardando...' : 'Guardar Todos los Cambios'}
                    </button>
                </div>

            </form>

            {/* Panel de Suscripción */}
            <div className="card-custom" style={{ marginTop: '20px' }}>
                <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FiTrendingUp /> Mi Suscripción
                </h3>

                {!company?.suscripcion ? (
                    <div style={{
                        background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)',
                        border: '1px solid #f3e8ff',
                        borderRadius: '16px',
                        padding: '24px',
                        marginBottom: '20px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 style={{ color: '#86198f', fontWeight: '700', margin: 0, fontSize: '1.25rem' }}>
                                    Plan Gratuito / Periodo de Prueba
                                </h4>
                                <p style={{ margin: '5px 0 0 0', color: '#a21caf', fontSize: '0.95rem' }}>
                                    Estado: <strong>TRIAL INICIAL (Usuario Antiguo)</strong>
                                </p>
                            </div>
                            <div>
                                <button
                                    className="btn-custom"
                                    onClick={() => navigate('/pricing')}
                                    style={{
                                        background: 'linear-gradient(135deg, #8e2de2, #4a00e0)',
                                        color: 'white',
                                        border: 'none',
                                        boxShadow: '0 4px 10px rgba(74, 0, 224, 0.3)'
                                    }}
                                >
                                    Elegir un Plan Pro
                                </button>
                            </div>
                        </div>
                        <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f0abfc' }}>
                            <p style={{ color: '#86198f', fontSize: '0.9rem', marginBottom: 0 }}>
                                *Al ser un usuario inicial antiguo en nuestra plataforma, estás en un periodo de prueba no medido pero con funcionalidades limitadas en el servidor. Recomendamos actualizar tu plan para evitar pausas en el servicio.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        background: company.suscripcion.estado === 'ACTIVA'
                            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                            : 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)',
                        border: company.suscripcion.estado === 'ACTIVA'
                            ? '1px solid #bbf7d0'
                            : '1px solid #f3e8ff',
                        borderRadius: '16px',
                        padding: '24px',
                        marginBottom: '20px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 style={{
                                    color: company.suscripcion.estado === 'ACTIVA' ? '#166534' : '#86198f',
                                    fontWeight: '700',
                                    margin: 0,
                                    fontSize: '1.25rem'
                                }}>
                                    {company.suscripcion.plan ? company.suscripcion.plan.nombre : 'Plan Gratuito / Trial'}
                                </h4>
                                <p style={{ margin: '5px 0 0 0', color: company.suscripcion.estado === 'ACTIVA' ? '#15803d' : '#a21caf', fontSize: '0.95rem' }}>
                                    Estado: <strong>{company.suscripcion.estado}</strong>
                                    {company.suscripcion.fecha_fin && (
                                        <span> | Vence el {new Date(company.suscripcion.fecha_fin).toLocaleDateString()}</span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <button
                                    className="btn-custom"
                                    onClick={() => navigate('/pricing')}
                                    style={{
                                        background: company.suscripcion.estado === 'ACTIVA' ? '#16a34a' : 'linear-gradient(135deg, #8e2de2, #4a00e0)',
                                        color: 'white',
                                        border: 'none',
                                        boxShadow: company.suscripcion.estado === 'ACTIVA' ? '0 4px 10px rgba(22, 163, 74, 0.3)' : '0 4px 10px rgba(74, 0, 224, 0.3)'
                                    }}
                                >
                                    {company.suscripcion.estado === 'ACTIVA' ? 'Gestionar Plan' : 'Mejorar Plan'}
                                </button>
                            </div>
                        </div>

                        {company.suscripcion.estado === 'TRIAL' && company.suscripcion.limites && (
                            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f0abfc' }}>
                                <h5 style={{ fontSize: '1rem', color: '#86198f', marginBottom: '15px', fontWeight: '600' }}>Uso de tu Periodo de Prueba</h5>
                                <div className="row g-3">
                                    <div className="col-12 col-md-4">
                                        <div style={{ background: 'white', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '5px' }}>Cotizaciones Creadas</div>
                                            <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#374151' }}>
                                                {company.suscripcion.limites.cotizaciones_creadas} / {company.suscripcion.limites.max_cotizaciones}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <div style={{ background: 'white', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '5px' }}>Clientes Creados</div>
                                            <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#374151' }}>
                                                {company.suscripcion.limites.clientes_creados} / {company.suscripcion.limites.max_clientes}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <div style={{ background: 'white', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '5px' }}>Productos Creados</div>
                                            <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#374151' }}>
                                                {company.suscripcion.limites.productos_creados} / {company.suscripcion.limites.max_productos}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal de Recorte de Imagen */}
            <Modal show={showCropModal} onHide={() => setShowCropModal(false)} size="lg" centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Ajustar Logo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ position: 'relative', width: '100%', height: '400px', backgroundColor: '#333' }}>
                        <Cropper
                            image={imageToCrop}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            cropShape="round"
                            showGrid={false}
                        />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <label className="form-label-custom">Zoom</label>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(e) => setZoom(e.target.value)}
                            className="form-range"
                            style={{ width: '100%', accentColor: 'var(--primary-orange)' }}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn-custom" onClick={() => setShowCropModal(false)} style={{ backgroundColor: '#fff', border: '1px solid var(--gray-300)' }}>
                        Cancelar
                    </button>
                    <button className="btn-custom btn-primary" onClick={handleConfirmCrop}>
                        Confirmar Recorte
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Settings;
